import {useRef, useState} from 'react';
import {captureMessage} from '@sentry/nextjs';

import {useGoogleMapsGeocode} from '@/hooks/useGoogleMapsGeocode';
import {
  type GeocodedAddress,
  type GeocodedAddressResult,
  type SavedAddress,
} from '@/utils/googleAutocomplete';
import {useEventBarV2, type WhereSelection} from '../EventBarV2Provider';
import {type RecentAddress} from '../recentlyUsedAddresses';
import {resolveGeocodeOutcome} from './helpers/whereGeocode';
import {useEventBarV2Tracking} from './useEventBarV2Tracking';

type UseWhereAddressSelectionArgs = {onSelected: () => void};

// Resolves a Where address into local `whereSelection` state (and surfaces client-side validation).
export const useWhereAddressSelection = ({onSelected}: UseWhereAddressSelectionArgs) => {
  const [incompleteAddress, setIncompleteAddress] = useState(false);
  const [addressesToVerify, setAddressesToVerify] = useState<GeocodedAddress[] | null>(null);

  const {fetchGeocodedAddress} = useGoogleMapsGeocode();
  const {trackAddressSelected, trackSavedAddressSelected, trackRecentAddressSelected} =
    useEventBarV2Tracking();
  const {setHasUnresolvedWhereAddress, setWhereSelection, whereResolutionSeqRef} = useEventBarV2();

  // Dedupes the geocode round-trip: concurrent callers share the one in-flight promise so a racing
  // caller (e.g. a dismissal resolve then a quick search click) gets the real outcome, not a false
  // negative.
  const inFlightRef = useRef<Promise<WhereSelection | null> | null>(null);

  const clearValidation = () => {
    setIncompleteAddress(false);
    setAddressesToVerify(null);
    setHasUnresolvedWhereAddress(false);
  };

  // A direct pick supersedes any typed address still geocoding, so its late result is dropped.
  const supersedePendingResolution = () => {
    whereResolutionSeqRef.current += 1;
  };

  // Surface the "complete address" banner without a geocode round-trip — used when a search is
  // attempted with an empty Where field, which can't be resolved to an address.
  const flagIncompleteAddress = () => {
    setAddressesToVerify(null);
    setIncompleteAddress(true);
    setHasUnresolvedWhereAddress(true);
  };

  // `advance` runs the onSelected step (move on to When). True for a direct selection/entry; false
  // when a search validates a typed address inline. `addressString` is the user-facing text tracked
  // as the selected address; only a direct pick (advance) is tracked as an "address selected".
  const selectGeocoded = (address: GeocodedAddress, advance = true, addressString?: string) => {
    clearValidation();
    setWhereSelection({kind: 'geocoded', geocoded: address});
    if (advance) {
      trackAddressSelected(
        addressString ?? `${address.street}, ${address.city}, ${address.state}`,
        {
          street: address.street,
          city: address.city,
          state: address.state,
        },
      );
      onSelected();
    }
  };

  const selectSavedAddress = (address: SavedAddress) => {
    supersedePendingResolution();
    clearValidation();
    setWhereSelection({kind: 'address', address});
    trackSavedAddressSelected(address.mainText, {
      street: address.street,
      city: address.city,
      state: address.state,
    });
    onSelected();
  };

  const selectRecentAddress = (recent: RecentAddress) => {
    supersedePendingResolution();
    clearValidation();
    setWhereSelection({kind: 'geocoded', geocoded: recent.geocoded});
    trackRecentAddressSelected(recent.displayValue, {
      street: recent.geocoded.street,
      city: recent.geocoded.city,
      state: recent.geocoded.state,
    });
    onSelected();
  };

  // A search-history cell in the Where panel offers only its address, so selecting one stages that
  // address and nothing else — the entry's date/time/headcount stay out of it. The caller fires the
  // history-card tracking event, so this adds none of its own.
  const selectSearchHistoryAddress = (address: GeocodedAddress) => {
    supersedePendingResolution();
    clearValidation();
    setWhereSelection({kind: 'geocoded', geocoded: address});
    onSelected();
  };

  const selectGeolocation = (result: GeocodedAddressResult) => {
    supersedePendingResolution();
    clearValidation();
    setWhereSelection({kind: 'geolocation', geocodedAddress: result});
    onSelected();
  };

  // Geocode a free-form string (typed value or a clicked Google prediction) and branch on the count.
  // Resolves to the usable selection (a single match) — and returns it so a search-time caller can
  // commit it without waiting for the `setWhereSelection` state update to render; null surfaced an
  // incomplete/ambiguous/failed state. Concurrent callers share the one in-flight promise, so a
  // racing caller gets the real outcome instead of a false negative; a joining caller inherits the
  // first caller's `advance`.
  const resolveTypedAddress = (
    addressString: string,
    advance: boolean,
  ): Promise<WhereSelection | null> => {
    const seq = whereResolutionSeqRef.current;
    const inFlight = (inFlightRef.current ??= (async () => {
      try {
        const outcome = resolveGeocodeOutcome(await fetchGeocodedAddress(addressString));
        // A reset (catch-all Apply/Discard, or the dismissal restore) bumped the token while the
        // geocode was in flight — the staged address was cleared out from under us, so drop this
        // now-stale outcome instead of re-staging a selection or surfacing late validation.
        if (whereResolutionSeqRef.current !== seq) return null;
        if (outcome.status === 'single') {
          selectGeocoded(outcome.address, advance, addressString);
          return {kind: 'geocoded', geocoded: outcome.address};
        }
        if (outcome.status === 'incomplete') {
          setAddressesToVerify(null);
          setIncompleteAddress(true);
        } else {
          setIncompleteAddress(false);
          setAddressesToVerify(outcome.candidates);
        }
        setHasUnresolvedWhereAddress(true);
        return null;
      } catch (error) {
        // A geocode network failure would otherwise reject up through the search click as an
        // unhandled rejection and kill the search silently. Log it, then surface the complete-address
        // banner so the panel stays open with actionable feedback.
        captureMessage('EventBarV2 Where geocode failed', {
          level: 'warning',
          extra: {addressString, error},
        });
        flagIncompleteAddress();
        return null;
      } finally {
        inFlightRef.current = null;
      }
    })());
    return inFlight;
  };

  const selectTypedAddress = (addressString: string) => resolveTypedAddress(addressString, true);

  // Search-time validation: a clean match resolves to its selection (no advance to When) so the
  // search can commit it directly; an incomplete/ambiguous result resolves null so the caller keeps
  // the panel open.
  const resolveTypedAddressForSearch = (addressString: string) =>
    resolveTypedAddress(addressString, false);

  return {
    incompleteAddress,
    addressesToVerify,
    clearValidation,
    flagIncompleteAddress,
    selectGeocoded,
    selectSavedAddress,
    selectRecentAddress,
    selectSearchHistoryAddress,
    selectGeolocation,
    selectTypedAddress,
    resolveTypedAddressForSearch,
  };
};
